import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Container, Stack, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { ScrollRestoration, matchPath, matchRoutes, useLocation, useNavigate } from "react-router-dom";
import { setupInterceptors } from "../api/axios";
import { useAuth } from "../AuthContext";
import { NavPortalRoots } from "../OrderFlow/utils/orderFlowUtils";
import { RedirectionOutlet } from "../RedirectionOutlet";
import { useEnvironment } from "../Shared/components/EnvironmentProvider/EnvironmentProvider";
import { Footer } from "../Shared/components/Footer/Footer";
import Loading from "../Shared/components/loading";
import { NavBar } from "../Shared/components/Navigation/NavBar";
import { Routes, isValidUrl } from "../Shared/components/Navigation/navigationUtils";
import { useTopBar } from "../Shared/components/TopBar/useTopBar";
import { useContainerSettings } from "../Shared/hooks/UseContainerSettings/useContainerSettings";
import { LogLevel } from "../Shared/hooks/useLogging/Logging-types";
import { useLogging } from "../Shared/hooks/useLogging/useLogging";
import { useRouteMatch } from "../Shared/hooks/useRouteMatch";
import { default_background_color } from "../Shared/utils/constants";
import { useGeneralSettingsQuery } from "../SWR/GeneralSettings/useGeneralSettingsQuery";
import { notFoundRoutes, routesThatHideHeader, routesThatUseBgColor, sffMobileRoutes } from "./RootLayout-route-utils";
export const RootLayout = () => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const theme = useTheme();
    const [watermarkExists, setWatermarkExists] = useState(false);
    useEffect(() => {
        var _a, _b;
        const watermarkUrl = (_b = (_a = theme.custom) === null || _a === void 0 ? void 0 : _a.background) === null || _b === void 0 ? void 0 : _b.watermark;
        if (!watermarkUrl) {
            setWatermarkExists(false);
            return;
        }
        const img = new window.Image();
        img.src = watermarkUrl;
        img.onload = () => setWatermarkExists(true);
        img.onerror = () => setWatermarkExists(false);
    }, [(_b = (_a = theme.custom) === null || _a === void 0 ? void 0 : _a.background) === null || _b === void 0 ? void 0 : _b.watermark]);
    const { containerPadding, containerWidth, isMobile, openedFromMobileApp } = useContainerSettings();
    const navigate = useNavigate();
    const location = useLocation();
    const { isGiftCardFlow, isAccountSection, isThemePage } = useRouteMatch();
    const authContext = useAuth();
    const { isAuthenticated } = authContext;
    const { datadogLog } = useLogging();
    useEffect(() => {
        setupInterceptors(navigate, location, authContext);
    }, []);
    const bgRouteMatch = matchRoutes(routesThatUseBgColor, location);
    const headerRouteMatch = matchRoutes(routesThatHideHeader, location);
    const sffRouteMatch = matchRoutes(sffMobileRoutes, location);
    const notFoundRouteMatch = matchRoutes(notFoundRoutes, location);
    const { generalSettings } = useGeneralSettingsQuery();
    const { componentToRender, usesTopBar } = useTopBar(generalSettings !== null && generalSettings !== void 0 ? generalSettings : {});
    const { loading: environmentLoading, environment } = useEnvironment();
    if (environmentLoading || !generalSettings) {
        return _jsx(Loading, { text: environment === "localhost" ? "Root Layout..." : undefined, viewportHeight: "75vh" });
    }
    const bgColorPath = (_d = (_c = bgRouteMatch === null || bgRouteMatch === void 0 ? void 0 : bgRouteMatch[0]) === null || _c === void 0 ? void 0 : _c.route) === null || _d === void 0 ? void 0 : _d.path;
    const headerPath = (_f = (_e = headerRouteMatch === null || headerRouteMatch === void 0 ? void 0 : headerRouteMatch[0]) === null || _e === void 0 ? void 0 : _e.route) === null || _f === void 0 ? void 0 : _f.path;
    const sffPath = (_h = (_g = sffRouteMatch === null || sffRouteMatch === void 0 ? void 0 : sffRouteMatch[0]) === null || _g === void 0 ? void 0 : _g.route) === null || _h === void 0 ? void 0 : _h.path;
    const notFoundPath = (_k = (_j = notFoundRouteMatch === null || notFoundRouteMatch === void 0 ? void 0 : notFoundRouteMatch[0]) === null || _j === void 0 ? void 0 : _j.route) === null || _k === void 0 ? void 0 : _k.path;
    const defaultRedirectUrl = generalSettings.DefaultRedirectUrl;
    let backgroundColor = bgColorPath === Routes.INDEX || bgColorPath === Routes.NOT_FOUND
        ? default_background_color
        : bgColorPath === Routes.LOGIN ||
            bgColorPath === Routes.REGISTER ||
            bgColorPath === Routes.EDIT_FLOW ||
            bgColorPath === Routes.SKU ||
            bgColorPath === Routes.CHECKOUT ||
            bgColorPath === Routes.THANK_YOU ||
            bgColorPath === Routes.ORDER_FLOW
            ? theme.palette.background.default
            : theme.palette.common.white;
    const isPaymentConfirmation = matchPath({ path: "/PaymentStatus" }, location.pathname);
    if (isPaymentConfirmation) {
        backgroundColor = "white";
    }
    if (isAccountSection) {
        backgroundColor = theme.palette.background.default;
    }
    const isLoginOrRegister = headerPath === Routes.INDEX || headerPath === Routes.LOGIN || headerPath === Routes.REGISTER;
    const isSffChromelessView = (sffPath === Routes.SKU ||
        sffPath === Routes.ORDER_FLOW ||
        sffPath === Routes.EDIT_FLOW ||
        sffPath === Routes.CHECKOUT ||
        sffPath === Routes.THANK_YOU) &&
        openedFromMobileApp;
    const isPageNotFound = notFoundPath === Routes.NOT_FOUND;
    // If a user tries to access a protected route without authentication they are redirected to login.
    // We want to hide the header in this case.
    const hideIfNotAuthenticated = isAccountSection && !isAuthenticated;
    const shouldHideHeader = isLoginOrRegister || isSffChromelessView || isPageNotFound || isPaymentConfirmation || hideIfNotAuthenticated;
    const shouldHideFooter = isSffChromelessView || isPaymentConfirmation;
    const isIndexRoute = matchPath({ path: Routes.INDEX }, location.pathname);
    const topBarContent = isGiftCardFlow ? generalSettings.TopBarContentGiftCard : generalSettings.TopBarContentOrderFlow;
    const showTopBar = usesTopBar && !!topBarContent && !shouldHideHeader && !isAccountSection && !isThemePage;
    //If the defaultRedirectUrl is configured and valid and the index route is hit send the customer to the defaultRedirectUrl.
    if (!!(isIndexRoute === null || isIndexRoute === void 0 ? void 0 : isIndexRoute.pathname) && defaultRedirectUrl) {
        if (isValidUrl(defaultRedirectUrl)) {
            window.location.replace(defaultRedirectUrl);
            return _jsx(_Fragment, {});
        }
        datadogLog(`DefaultRedirectUrl is not a valid URL. Value: ${defaultRedirectUrl} `, LogLevel.WARN);
    }
    // ===== CONTAINER SETTINGS =====
    const containerMargins = isMobile ? {} : { mt: 1.5, mb: 1.5 };
    // This creates the "paper" effect for the account pages.
    const swankyStyles = isAccountSection
        ? Object.assign({ boxShadow: 3, backgroundColor: theme.palette.background.paper, borderRadius: 2 }, containerMargins) : {};
    const getWatermarkStyles = () => {
        var _a, _b;
        const pos = ((_b = (_a = theme.custom) === null || _a === void 0 ? void 0 : _a.background) === null || _b === void 0 ? void 0 : _b.watermarkPosition) || "center center";
        const spacing = theme.spacing(4);
        // Split into vertical and horizontal
        const [vertical, horizontal] = pos.split(" ");
        const getTransform = () => {
            const translateX = horizontal === "center" ? "-50%" : horizontal === "right" ? "-100%" : "0%";
            const translateY = vertical === "center" ? "-50%" : vertical === "bottom" ? "-50%" : "0%";
            return `translate(${translateX}, ${translateY})`;
        };
        return {
            position: "fixed",
            top: vertical === "top" ? "auto" : vertical === "bottom" ? "auto" : "50%",
            bottom: vertical === "bottom" ? spacing : "auto",
            left: horizontal === "left" ? "auto" : horizontal === "right" ? "auto" : "50%",
            right: horizontal === "right" ? spacing : "auto",
            transform: getTransform(),
            width: "200px",
            height: "200px",
            objectFit: "contain",
            zIndex: 0,
            pointerEvents: "none", // Don't interfere with interactions
        };
    };
    return (_jsxs(Stack, Object.assign({ id: "layout-wrapper", minHeight: "100vh", bgcolor: backgroundColor }, { children: [!shouldHideHeader && (_jsx(NavBar, { linksContent: componentToRender, showTopBar: showTopBar, content: topBarContent, 
                // TODO! @MUI -- not yet integrated into theme
                topBarBackgroundColor: generalSettings.TopBarBackgroundColor })), _jsxs(Container, Object.assign({ id: "content-container", maxWidth: containerWidth, disableGutters: isMobile || openedFromMobileApp ? true : false, sx: Object.assign(Object.assign({ flexGrow: 1, position: "relative" }, containerPadding), swankyStyles) }, { children: [watermarkExists && _jsx("img", { src: theme.custom.background.watermark, alt: "", style: getWatermarkStyles() }), _jsxs(Stack, Object.assign({ id: "main-content-wrapper", minHeight: "100%", justifyContent: "center", flexGrow: 1, sx: {
                            position: "relative",
                            zIndex: 1,
                        } }, { children: [_jsx(RedirectionOutlet, {}), _jsx(ScrollRestoration, {})] }))] })), _jsx("div", { id: NavPortalRoots.Pinned, style: { position: "sticky", bottom: 0, zIndex: 1100 } }), _jsx("div", { id: NavPortalRoots.Unpinned, style: { width: "100%" } }), !shouldHideFooter && (_jsx(Box, Object.assign({ id: "footer-wrapper" }, { children: _jsx(Footer, {}) })))] })));
};
